import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface Segment {
  id: string
  name: string
  description: string
  criteria: Record<string, any>
  customer_count: number
  avg_revenue: number
  avg_orders: number
  growth_rate: number
  color: string
  created_at: string
}

export default function CustomerSegmentationPage() {
  const [segments, setSegments] = useState<Segment[]>([])
  const [statistics, setStatistics] = useState<{total_segments: number; total_customers: number; avg_segment_size: number; top_segment: string} | null>(null)
  const [selectedSegment, setSelectedSegment] = useState<Segment | null>(null)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    const [segRes, statsRes] = await Promise.all([
      fetch(API_BASE + '/segments'),
      fetch(API_BASE + '/segments/statistics')
    ])
    if (segRes.ok) setSegments((await segRes.json()).segments || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
  }

  const formatCurrency = (val: number) => '$' + val.toLocaleString()

  const getGrowthColor = (rate: number) => {
    if (rate > 10) return 'text-green-600'
    if (rate > 0) return 'text-green-500'
    if (rate > -10) return 'text-red-500'
    return 'text-red-600'
  }

  const totalCustomers = segments.reduce((sum, s) => sum + s.customer_count, 0)

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-8">Customer Segmentation</h1>

        {statistics && (
          <div className="grid grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="text-sm text-gray-500">Total Segments</div>
              <div className="text-3xl font-bold">{statistics.total_segments}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="text-sm text-gray-500">Total Customers</div>
              <div className="text-3xl font-bold">{statistics.total_customers.toLocaleString()}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="text-sm text-gray-500">Avg Segment Size</div>
              <div className="text-3xl font-bold">{statistics.avg_segment_size.toLocaleString()}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="text-sm text-gray-500">Top Segment</div>
              <div className="text-xl font-bold text-blue-600">{statistics.top_segment}</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2 space-y-6">
            {/* Segment Distribution */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold mb-4">Segment Distribution</h2>
              <div className="h-8 rounded-lg overflow-hidden flex">
                {segments.map(seg => (
                  <div
                    key={seg.id}
                    style={{
                      width: `${(seg.customer_count / totalCustomers) * 100}%`,
                      backgroundColor: seg.color
                    }}
                    className="cursor-pointer hover:opacity-80 transition-opacity"
                    onClick={() => setSelectedSegment(seg)}
                    title={`${seg.name}: ${seg.customer_count} customers`}
                  />
                ))}
              </div>
              <div className="flex flex-wrap gap-4 mt-4">
                {segments.map(seg => (
                  <div key={seg.id} className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded" style={{ backgroundColor: seg.color }} />
                    <span className="text-sm">{seg.name}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Segments Table */}
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b flex justify-between items-center">
                <h2 className="text-xl font-semibold">All Segments</h2>
                <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                  + Create Segment
                </button>
              </div>
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Segment</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customers</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Avg Revenue</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Avg Orders</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Growth</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {segments.map(seg => (
                    <tr
                      key={seg.id}
                      className={`hover:bg-gray-50 cursor-pointer ${selectedSegment?.id === seg.id ? 'bg-blue-50' : ''}`}
                      onClick={() => setSelectedSegment(seg)}
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-3 h-3 rounded" style={{ backgroundColor: seg.color }} />
                          <div>
                            <div className="font-medium">{seg.name}</div>
                            <div className="text-xs text-gray-500">{seg.description}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">{seg.customer_count.toLocaleString()}</td>
                      <td className="px-6 py-4">{formatCurrency(seg.avg_revenue)}</td>
                      <td className="px-6 py-4">{seg.avg_orders}</td>
                      <td className="px-6 py-4">
                        <span className={getGrowthColor(seg.growth_rate)}>
                          {seg.growth_rate > 0 ? '+' : ''}{seg.growth_rate}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div>
            {selectedSegment ? (
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-4 h-4 rounded" style={{ backgroundColor: selectedSegment.color }} />
                  <h3 className="text-lg font-semibold">{selectedSegment.name}</h3>
                </div>
                <div className="space-y-4">
                  <div>
                    <div className="text-sm text-gray-500">Description</div>
                    <div className="text-sm">{selectedSegment.description}</div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-sm text-gray-500">Customers</div>
                      <div className="text-xl font-bold">{selectedSegment.customer_count.toLocaleString()}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500">Avg Revenue</div>
                      <div className="text-xl font-bold">{formatCurrency(selectedSegment.avg_revenue)}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500">Avg Orders</div>
                      <div className="text-xl font-bold">{selectedSegment.avg_orders}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500">Growth Rate</div>
                      <div className={`text-xl font-bold ${getGrowthColor(selectedSegment.growth_rate)}`}>
                        {selectedSegment.growth_rate > 0 ? '+' : ''}{selectedSegment.growth_rate}%
                      </div>
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500 mb-2">Segment Criteria</div>
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <pre className="text-xs overflow-auto">
                        {JSON.stringify(selectedSegment.criteria, null, 2)}
                      </pre>
                    </div>
                  </div>
                  <div className="pt-4 space-y-2">
                    <button className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                      View Customers
                    </button>
                    <button className="w-full px-4 py-2 border rounded-lg hover:bg-gray-50">
                      Edit Segment
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-4">Segment Insights</h3>
                <div className="space-y-4">
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <div className="font-medium text-blue-800">High Value Opportunity</div>
                    <div className="text-sm text-blue-600 mt-1">
                      Enterprise segment shows 25% growth potential
                    </div>
                  </div>
                  <div className="p-4 bg-yellow-50 rounded-lg">
                    <div className="font-medium text-yellow-800">Attention Needed</div>
                    <div className="text-sm text-yellow-600 mt-1">
                      Churning segment requires retention campaign
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  )
}
